import { useEffect, useState } from "react";
import axios from "axios";
import Big from "big.js";
import moment from "moment";
import Uri from "jsuri";
import isNumber from "lodash/isNumber";
import {
  SERVICE_URL,
  HEADOFFICE_OPTION_VALUE,
  ASSET_AVAILABILITY_URL,
  PART_AVAILABILITY_URL
} from "constants/AppConstants";
import {
  ASSET_TYPE,
  isValidNumber,
  SrmBoolean,
  TRANSACTION_MODE,
  TRANSACTION_TYPE
} from "helpers/helper";
import {
  FLASK_DATE_FORMAT,
  LONG_DATE_TIME_FORMAT,
  SRM_DATE_FORMAT
} from "helpers/timeHelpers";
import { FlaskReq } from "helpers/Request";
import { useRequest } from "helpers/hooks";
import {
  Product,
  normalizeProduct
} from "../contracts/sections/contractproductselect/ProductServiceUtils";

/** (A=Any, L=Labor, W=Work Order) */
type ServiceType = "A" | "L" | "W" | string;

type Y_OR_N = "Y" | "N" | "";

interface Service {
  allowEditAutoSvc?: string;
  allowPo?: string;
  alternateDescription?: string;
  amount?: number;
  commissionable?: Y_OR_N;
  description: string;
  glAccount?: string;
  glWarrantyRevenue?: string;
  quantityPrompt?: Y_OR_N;
  serviceCode: string;
  tax1Code?: string;
  tax2Code?: string;
  type?: ServiceType | string;
  listPrice?: number;
  /** Unique ID associated to this invoice detail. */
  detailTrxN?: string;
  quantity?: number;
}

enum PrintEnum {
  Print = "Y",
  DoNotPrint = "N",
  HidePricing = "Y"
}

interface Detail {
  /** Transaction Type (R=Rental, S=Non-Serialized Sale, V=Service) */
  type: Readonly<TRANSACTION_TYPE>;
  /** Product Number/Barcode/Service Code  */
  product: string;
  description: string;
  quantity?: number;
  /** Date Out (YYYYMMDD) */
  dateOut?: string;
  /** Time Out (HHMMSS) */
  timeOut?: string;
  /** Date In (YYYYMMDD) */
  dateIn?: string;
  /** Time In (HHMMSS) */
  timeIn?: string;
  listPrice: number;
  serialNumber?: string;
  discountAmount?: number;
  discP?: number;
  print?: PrintEnum;
  /** Unique ID associated to this invoice detail. */
  detailTrxN?: string;
  autoService?: string;
  /** Y=Detail was manually added and was not from original source. */
  misc?: string;
  contractNumber?: string;
  /** Unique ID associated to the contract detail from which this invoice detail was generated from. */
  contractDetailTrxN?: string;
  notes?: {
    note: string;
    print: "Y" | "N" | string;
  }[];
  serviceCode?: string;
}

export function detailToService(detail: Readonly<Detail>): Service {
  let service: Service = {
    ...detail,
    serviceCode: detail.product.trim(),
    amount: Number(detail.listPrice)
  };
  delete service.type;
  delete service.listPrice;

  return service;
}

export function serviceToDetail(service: Readonly<Service>): Detail {
  let detail: Detail = {
    ...service,
    type: TRANSACTION_TYPE.SERVICE,
    product: service.serviceCode.trim(),
    listPrice: service.amount
  };

  delete detail.serviceCode;

  return detail;
}

export function calculateServiceTotal(service: Readonly<Service>): number {
  let cost = Number(service.amount) || 0;

  return Number(
    Big(cost).times(isValidNumber(service.quantity) ? service.quantity : 1)
  );
}

export function useLocation() {
  const [location, setLocation] = useState("");

  useEffect(() => {
    const onLocationChange = e => setLocation(e.detail);

    document.addEventListener("locationChange", onLocationChange);

    return () => {
      document.removeEventListener("locationChange", onLocationChange);
    };
  }, []);

  return location;
}

export function useDivision() {
  const [division, setDivision] = useState("");

  useEffect(() => {
    const onDivisionChange = e => setDivision(e.detail);

    document.addEventListener("divisionChange", onDivisionChange);

    return () => {
      document.removeEventListener("divisionChange", onDivisionChange);
    };
  }, []);

  return division;
}

export function useWeekChange({
  format = FLASK_DATE_FORMAT
}: {
  format?:
    | typeof FLASK_DATE_FORMAT
    | typeof LONG_DATE_TIME_FORMAT
    | typeof SRM_DATE_FORMAT;
} = {}) {
  const [week, setWeek] = useState({
    from_date: moment().startOf("week").add(1, "days").format(format),
    to_date: moment().startOf("week").add(7, "days").format(format)
  });

  useEffect(() => {
    const onWeekChange = e => setWeek(e.detail);

    document.addEventListener("weekChange", onWeekChange);

    return () => {
      document.removeEventListener("weekChange", onWeekChange);
    };
  }, []);

  return week;
}

export async function updateQtyPrompt(services: any[]) {
  let newServices = [];
  await Promise.all(
    [...new Set(services.map(service => service.product.trim()))].map(
      async serviceCode => {
        try {
          const res = await axios.get(
            `${SERVICE_URL}${encodeURIComponent(serviceCode)}`,
            {
              responseType: "json"
            }
          );

          newServices = [
            ...newServices,
            ...services
              .filter(s => s.product.trim() === serviceCode)
              .map(s => ({ ...s, qtyPrompt: res.data.services[0].qtyPrompt }))
          ];
        } catch (err) {
          console.log(err);
        }
      }
    )
  );

  return newServices;
}

export function getSelectedProductsGetter(prevDetails, productsToSubmit) {
  const prevProducts = (prevDetails || []).filter(
    d => d.type === ASSET_TYPE.Rental || d.type === ASSET_TYPE.Sales
  );
  let selectedProducts = (productsToSubmit || []).map(prod => {
    let product: Product = {
      contractDetailTrxN: prod.contractDetailTrxN,
      type: prod.type,
      product: prod.product,
      description: prod.description,
      detailTrxN: prod.detailTrxN || undefined,
      quantity: prod.quantity,
      dateOut: prod.dateOut || "",
      timeOut: prod.timeOut || "",
      dateIn: prod.dateIn || "",
      timeIn: prod.timeIn || "",
      print: SrmBoolean.True,
      rates: prod.rates?.map(p => {
        p.code = p.rateCode;
        return p;
      }),
      meterCharge: prod.meterCharge,
      availability: prod.availability
    };
    try {
      if (
        prod.overrideListPrice ||
        Number(prod.rate) !== Number(prod.listPrice)
      ) {
        product.listPrice = prod.listPrice;
      }
    } catch (e) {
      console.error(e);
    }

    /*
      listPrice must be overriden for invoices.
      Contracts will recalculate price.
      */
    if (product.type === ASSET_TYPE.Rental) {
      delete product.listPrice;
      if (
        prod.overrideListPrice ||
        Number(prod.netUnitRate) !== Number(prod.unitTotal)
      ) {
        product.listPrice = Number(prod.unitTotal);
      }
    }

    if (prod.serialNumber) {
      product.serialNumber = prod.serialNumber;
    }

    if (prod.discP && !prod.overrideListPrice) {
      product.discP = prod.discP;
      delete product.listPrice;
    }

    const hasPrevDetail = prevProducts.some(
      d => d.product.trim() === prod.product.trim()
    );

    product.mode = hasPrevDetail
      ? TRANSACTION_MODE.CHANGE
      : TRANSACTION_MODE.ADD;

    if (prod.hasOwnProperty("mode")) {
      product.mode = prod.mode;
    }

    return product;
  });

  const deletedProducts = prevProducts
    .filter(
      p =>
        selectedProducts.findIndex(
          ({ product }) => product.trim() === p.product.trim()
        ) === -1
    )
    .map(p => {
      p.mode = TRANSACTION_MODE.DELETE;
      return p;
    });

  return selectedProducts.concat(deletedProducts);
}

export function getSubtotal({
  rentalDisc = 0,
  salesDisc = 0,
  productsToSubmit = []
}) {
  const getInvoicePrice = p => {
    if (p.type === ASSET_TYPE.Rental) {
      return Big(p.meterCharge || 0).plus(p.unitTotal || 0);
    }
    return isValidNumber(p.listPrice) ? p.listPrice : 0;
  };

  const getPrice = getInvoicePrice;

  function getProductTotal(type) {
    return productsToSubmit
      .filter(p => p.type === type)
      .map(p => {
        const { quantity, discP } = p;
        const price = getPrice(p);
        const rate = Big(isValidNumber(price) ? price : 0);

        let value = isValidNumber(quantity) ? rate.times(quantity) : rate;
        if (isValidNumber(discP) && !p.overrideListPrice) {
          value = value.times(100 - (discP || 0)).times(0.01);
        }

        return Number(value.toFixed(2));
      })
      .filter(Number.isFinite)
      .reduce((accumulator, currVal) => {
        try {
          accumulator = accumulator.plus(currVal);
        } catch (e) {
          // big.js throws an error if the number is invalid
        }
        return accumulator;
      }, Big(0));
  }

  let rentalTotal = getProductTotal(ASSET_TYPE.Rental);
  if (rentalTotal.gt(0)) {
    rentalTotal = rentalTotal.times(
      Big(100)
        .minus(isValidNumber(rentalDisc) ? rentalDisc : 0)
        .div(100)
    );
  }

  let salesTotal = getProductTotal(ASSET_TYPE.Sales);
  if (salesTotal.gt(0)) {
    salesTotal = salesTotal.times(
      Big(100)
        .minus(isValidNumber(salesDisc) ? salesDisc : 0)
        .div(100)
    );
  }

  return rentalTotal.plus(salesTotal).toFixed(2);
}

export function getServiceSubTotal(services) {
  return services
    .reduce((accumulator, currVal) => {
      try {
        accumulator = accumulator.plus(
          Big(
            isNumber(currVal.listPrice) ? currVal.listPrice : currVal.amount
          ).times(currVal.quantity)
        );
      } catch (e) {}
      return accumulator;
    }, Big(0))
    .toFixed(2);
}

export function useGetProducts({
  setIsFetchingData,
  type,
  state,
  pageSize,
  query,
  setState
}) {
  const [fetchAvailability] = useRequest({ flask: false });

  useEffect(() => {
    const source = axios.CancelToken.source();
    setIsFetchingData(true);

    const url = new Uri(`${localStorage.flask_url}/${type}/`)
      .addQueryParam("per_page", pageSize)
      .addQueryParam("page", state.page)
      .addQueryParam("exclude_non_bulk_disposal", true)
      .addQueryParam("inactive", SrmBoolean.False)
      .addQueryParam("expand", ["serialNumbers"].toString());

    if (query !== "") {
      url.addQueryParam("search", query);
    }

    FlaskReq({
      method: "get",
      url: url.toString(),
      cancelToken: source.token
    }).then(res => {
      if (res?.data !== undefined) {
        const products = (
          type === "parts"
            ? res.data.parts.map(part => {
                part.type = ASSET_TYPE.Sales;
                return part;
              })
            : res.data.assets.map(asset => {
                asset.type = ASSET_TYPE.Rental;
                return asset;
              })
        ).map(normalizeProduct);

        setState({
          products: products,
          pages: res.data.pages.pages,
          dataTimestamp: Date.now()
        });
        fetchProductAvailability(products);
      }
      setIsFetchingData(false);
    });
    return () => source.cancel();
  }, [query, state.page, type]);

  async function fetchProductAvailability(productList) {
    const selectedLocation = state.location
      ? state.location
      : HEADOFFICE_OPTION_VALUE;

    const fetchUrl =
      type === "parts" ? PART_AVAILABILITY_URL : ASSET_AVAILABILITY_URL;

    const url = new Uri(fetchUrl)
      .addQueryParam("avail_location", selectedLocation)
      .addQueryParam(
        "product_numbers",
        [...new Set(productList.map(product => product.product))].join(",")
      );
    try {
      const res = await fetchAvailability(url.toString());
      if (!res.data) {
        return;
      }

      const updatedProducts = productList.map(product => {
        const availability = res.data.find(
          item => item.number === product.product
        );
        if (availability) {
          return { ...product, availability };
        }
        return product;
      });
      setState({
        products: updatedProducts
      });
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
      }
    }
  }
}
